import { useCallback, useEffect, useMemo, useState } from 'react';

import { useDebouncedRemoteSearch } from '@/components/shared/use-debounced-remote-search';
import { fetchPublishedPages } from '@/lib/pages';
import { usePagesActions } from '@/stores/pages-store';
import type { Page } from '@/types/page';

interface UsePublishedPageSearchProps {
  enabled: boolean;
  selectedPageId?: string;
  initialPages: Page[];
  hasInitialPagesLoaded: boolean;
  isInitialLoading?: boolean;
}

export function usePublishedPageSearch({
  enabled,
  selectedPageId,
  initialPages,
  hasInitialPagesLoaded,
  isInitialLoading = false,
}: UsePublishedPageSearchProps) {
  const { loadPages } = usePagesActions();
  const [searchQuery, setSearchQuery] = useState('');
  const [includedSelectedPage, setIncludedSelectedPage] = useState<Page | null>(null);

  const selectedInInitial = Boolean(
    selectedPageId && initialPages.some((page) => page.id === selectedPageId),
  );

  useEffect(() => {
    if (!enabled) {
      setSearchQuery('');
    }
  }, [enabled]);

  useEffect(() => {
    if (enabled && !hasInitialPagesLoaded && !isInitialLoading) {
      loadPages();
    }
  }, [enabled, hasInitialPagesLoaded, isInitialLoading, loadPages]);

  const searchPublishedPages = useCallback(
    async (query: string) => {
      try {
        return await fetchPublishedPages(query, { includePageId: selectedPageId });
      } catch {
        return [];
      }
    },
    [selectedPageId],
  );

  const {
    data: remotePages,
    isLoading: isRemoteLoading,
    normalizedQuery,
  } = useDebouncedRemoteSearch({
    enabled,
    query: searchQuery,
    search: searchPublishedPages,
  });

  useEffect(() => {
    if (!enabled || !selectedPageId || !hasInitialPagesLoaded || selectedInInitial) {
      setIncludedSelectedPage(null);
      return;
    }

    let cancelled = false;

    fetchPublishedPages('', { includePageId: selectedPageId })
      .then((pages) => {
        if (cancelled) return;
        setIncludedSelectedPage(pages.find((page) => page.id === selectedPageId) ?? null);
      })
      .catch(() => {
        if (cancelled) return;
        setIncludedSelectedPage(null);
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, hasInitialPagesLoaded, selectedInInitial, selectedPageId]);

  const basePages = normalizedQuery ? (remotePages ?? []) : initialPages;

  const selectedPage = useMemo(() => {
    if (!selectedPageId) return null;

    return (
      initialPages.find((page) => page.id === selectedPageId) ||
      (includedSelectedPage?.id === selectedPageId ? includedSelectedPage : null) ||
      remotePages?.find((page) => page.id === selectedPageId) ||
      null
    );
  }, [includedSelectedPage, initialPages, remotePages, selectedPageId]);

  const pages = useMemo(() => {
    if (!selectedPageId || !selectedPage) {
      return basePages;
    }

    if (basePages.some((page) => page.id === selectedPageId)) {
      return basePages;
    }

    return [selectedPage, ...basePages];
  }, [basePages, selectedPage, selectedPageId]);

  const isSearchingRemotely = normalizedQuery.length > 0;

  return {
    pages,
    searchQuery,
    setSearchQuery,
    hasLoaded: enabled && (isSearchingRemotely ? remotePages !== null : hasInitialPagesLoaded),
    isLoading: isSearchingRemotely
      ? isRemoteLoading
      : enabled && isInitialLoading && !hasInitialPagesLoaded,
  };
}

export function getPublishedPageSearchEmptyText({
  hasLoaded,
  searchQuery,
}: {
  hasLoaded: boolean;
  searchQuery: string;
}): string {
  if (!hasLoaded) return 'Loading pages...';
  return searchQuery.trim() ? 'No matching pages found.' : 'No published pages found.';
}
